import { Injectable } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { Observable, from, of, throwError, BehaviorSubject } from 'rxjs';
import { catchError, map, retry, switchMap, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { captureException } from '@sentry/angular';
import { environment } from '../../environments/environment';
import { URLService } from './dynamic-url.service';
import { TelemetryService } from './telemetry.service';

export enum NotificationPermissionStatus {
  GRANTED = 'granted',
  DENIED = 'denied',
  DEFAULT = 'default', // User hasn't made a decision yet
  UNSUPPORTED = 'unsupported',
}

// Response from server after saving subscription
export interface NotificationSubscriptionResponse {
  message: string;
  subscription_id?: string;
  error?: string;
}

const httpOptions = {
  headers: {
    'Content-Type': 'application/json',
  },
};

@Injectable({
  providedIn: 'root',
})
export class PushNotificationsService {
  // Delay before showing notification permission prompt (in milliseconds)
  public static readonly NOTIFICATION_PROMPT_DELAY_MS = 10 * 1000; // 10 seconds
  private _permissionStatus!: BehaviorSubject<NotificationPermissionStatus>;

  private readonly _isPushSupported: boolean;

  // Observable to track the current permission status
  public permissionStatus$!: Observable<NotificationPermissionStatus>;

  // Observable for tracking if push is supported
  public pushSupported = this.swPush.isEnabled;

  constructor(
    private swPush: SwPush,
    private http: HttpClient,
    private urlService: URLService,
    private telemetry: TelemetryService,
  ) {
    // Initialize push support status first (must be before _permissionStatus)
    this._isPushSupported = this._computePushSupportStatus();

    // Now initialize _permissionStatus (after _isPushSupported is ready)
    this._permissionStatus = new BehaviorSubject<NotificationPermissionStatus>(
      this.getInitialPermissionStatus(),
    );
    this.permissionStatus$ = this._permissionStatus.asObservable();

    // Listen for permission changes (browsers may support this)
    if (typeof Notification !== 'undefined' && Notification.permission) {
      try {
        navigator.permissions?.query({ name: 'notifications' })?.then((permissionStatus) => {
          permissionStatus.onchange = () => {
            this.updatePermissionStatus();
          };
        });
      } catch (error) {
        captureException(new Error('Permission status monitoring not supported'), {
          extra: { error },
        });
      }
    }

    // Listen for push messages
    this.swPush.messages.subscribe((message) => {
      // Track notification received event
      this.telemetry.event('notification_received', { type: 'push' });
    });

    // Listen for notification clicks through the service worker
    this.swPush.notificationClicks
      .subscribe(({ action, notification }) => {
        // Track notification click event
        this.telemetry.event('notification_clicked', {
          action: action || 'default',
          notificationType: 'push',
        });
      });
  }

  /**
   * Get the initial permission status when the service loads
   */
  private getInitialPermissionStatus(): NotificationPermissionStatus {
    if (!this.isPushSupported) {
      return NotificationPermissionStatus.UNSUPPORTED;
    }

    return Notification.permission as NotificationPermissionStatus;
  }

  /**
   * Update the permission status BehaviorSubject
   */
  public updatePermissionStatus(): void {
    if (!this.isPushSupported) {
      this._permissionStatus.next(NotificationPermissionStatus.UNSUPPORTED);
      return;
    }

    const permission = Notification.permission as NotificationPermissionStatus;
    this._permissionStatus.next(permission);
  }

  /**
   * Check if push notifications are supported in this browser
   */
  public get isPushSupported(): boolean {
    return this._isPushSupported;
  }

  /**
   * Check if push notifications are supported in this browser
   */
  private _computePushSupportStatus(): boolean {
    const supported =
      this.swPush.isEnabled &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      typeof Notification !== 'undefined';

    if (!supported) {
      const details = {
        swPushEnabled: this.swPush.isEnabled,
        serviceWorkerSupported: 'serviceWorker' in navigator,
        pushManagerSupported: 'PushManager' in window,
        notificationSupported: typeof Notification !== 'undefined',
      };

      this.telemetry.event('notification_unsupported', details);
    }

    return supported;
  }

  /**
   * Request permission for notifications and subscribe if granted
   */
  public requestPermissionAndSubscribe(): Observable<PushSubscription | null> {
    if (!this.isPushSupported) {
      this.telemetry.event('notification_unsupported', {});
      return throwError(() => new Error('Push notifications are not supported in this browser'));
    }

    // If already granted, just subscribe
    if (Notification.permission === NotificationPermissionStatus.GRANTED) {
      return this.subscribeToPush();
    }

    // Otherwise, request permission first
    return from(Notification.requestPermission()).pipe(
      tap((permission) => {
        this._permissionStatus.next(permission as NotificationPermissionStatus);
        this.telemetry.event('notification_permission', { status: permission });
      }),
      switchMap((permission) => {
        if (permission === NotificationPermissionStatus.GRANTED) {
          return this.subscribeToPush();
        } else {
          return of(null);
        }
      }),
      catchError((error) => {
        this.telemetry.event('notification_error', {
          type: 'permission_request',
          error: error.message || 'Unknown error',
        });
        return throwError(() => error);
      }),
    );
  }

  /**
   * Subscribe to push notifications using SwPush
   */
  private subscribeToPush(): Observable<PushSubscription> {
    return from(
      this.swPush.requestSubscription({
        serverPublicKey: environment.vapidPublicKey,
      }),
    ).pipe(
      tap((sub) => {
        this.telemetry.event('notification_subscribed', {});
      }),
      switchMap((sub) => this.saveSubscription(sub).pipe(map(() => sub))),
      catchError((error) => {
        if (error.name === 'NotSupportedError') {
          // Handle case where push service is unavailable
          captureException(new Error('Push service is unavailable or the permission was denied'), {
            extra: { error },
          });
          this.telemetry.event('notification_error', {
            type: 'subscription_request',
            error: 'push_service_unavailable',
          });
        } else {
          captureException(new Error('Error subscribing to push notifications'), {
            extra: { error },
          });
          this.telemetry.event('notification_error', {
            type: 'subscription_request',
            error: error.message || 'Unknown error',
          });
        }
        return throwError(() => error);
      }),
    );
  }

  /**
   * Check if we already have an active push subscription
   */
  public getExistingSubscription(): Observable<PushSubscription | null> {
    if (!this.isPushSupported) {
      return of(null);
    }

    return from(this.swPush.subscription);
  }

  /**
   * Unsubscribe from push notifications
   */
  public unsubscribe(): Observable<boolean> {
    return this.getExistingSubscription().pipe(
      switchMap((subscription) => {
        if (!subscription) {
          return of(true);
        }

        // The native browser PushSubscription has an unsubscribe method
        return from(this.swPush.unsubscribe()).pipe(
          tap(() => {
            this.telemetry.event('notification_unsubscribed', {});
          }),
          // Also inform the server about the unsubscription
          switchMap(() => this.deleteSubscription(subscription)),
          map(() => true),
          catchError((error) => {
            captureException(new Error('Error unsubscribing from push notifications'), {
              extra: { error },
            });

            return throwError(() => error);
          }),
        );
      }),
    );
  }

  /**
   * Save the subscription on the server
   */
  private saveSubscription(
    subscription: PushSubscription,
  ): Observable<NotificationSubscriptionResponse> {
    const notificationUrl = `${this.urlService.getDynamicUrl()}/tutor/saveSubscription`;

    return this.http
      .post<NotificationSubscriptionResponse>(
        notificationUrl,
        JSON.stringify({ subscription }),
        httpOptions,
      )
      .pipe(
        retry(3),
        catchError((error) => {
          captureException(new Error('Error saving push subscription on server'), {
            extra: { error },
          });
          this.telemetry.event('notification_error', {
            type: 'save_subscription',
            error: error.message || 'Server error',
          });
          return throwError(() => error);
        }),
      );
  }

  /**
   * Delete the subscription from the server when unsubscribing
   */
  private deleteSubscription(subscription: PushSubscription): Observable<any> {
    // We'll encode the endpoint as it's used as an identifier on the server
    const endpoint = encodeURIComponent(subscription.endpoint);
    const deleteUrl = `${this.urlService.getDynamicUrl()}/tutor/notifications?endpoint=${endpoint}`;

    return this.http.delete(deleteUrl).pipe(
      retry(3),
      catchError((error) => {
        captureException(new Error('Error deleting push subscription from server'), {
          extra: { error },
        });
        return throwError(() => error);
      }),
    );
  }
}
